import { AfterViewInit, Component, OnInit } from '@angular/core';
import { GlobalStateModel } from '../../../../store/state.model';
import { PwaSettings } from '../../../../vendors/directus/interfaces/pwa-settings.interface';
import { firstValueFrom, Observable, of, timer } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { SplashScreen } from '@capacitor/splash-screen';
import { environment } from '../../../../environments/environment';
import { Capacitor } from '@capacitor/core';
import { BundleInfo, CapacitorUpdater, latestVersion } from '@appmassive/capacitor-updater';
import { ModalController } from '@ionic/angular';
import { catchError, filter, take } from 'rxjs/operators';
import { Preferences as Storage } from '@capacitor/preferences';
import { OnboardingSlidesComponent } from '@common/components';
import { Onboarding } from '../../../../vendors/directus/interfaces/onboarding.interface';
import { MainSettings } from '../../../../vendors/directus/interfaces/main-settings.interface';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { InitializeUser, LogOut, SetAdditionalUserFields, SignIn } from '../../../../store/actions/user.actions';
import { TokenManagerService } from '@common/services';

enum DownloadState {
  CHECKING,
  FOUND,
  DOWNLOADING,
}

@Component({
  selector: 'app-appmassive-updater',
  templateUrl: './appmassive-updater.component.html',
  styleUrls: ['appmassive-updater.component.scss'],
})
export class AppmassiveUpdaterComponent implements AfterViewInit {
  @Select((state: GlobalStateModel) => state.app.splashScreen) splashScreen$: Observable<PwaSettings>;
  @Select((state: GlobalStateModel) => state.app.onboardingSlides) onboardingSlides$: Observable<Onboarding[]>;
  @Select((state: GlobalStateModel) => state.app.mainSettings) mainSettings$: Observable<MainSettings>;

  constructor(
    private modalCtrl: ModalController,
    private store: Store,
    private tokenManager: TokenManagerService
  ) {}

  currentState = DownloadState.CHECKING;
  downloadState = DownloadState; // for template use
  capacitor = Capacitor; // for template use

  checkingCopy = 'Checking for fresh goodness…🥖';

  newVersionFoundCopy = 'Deliciousness found…✨';

  private downloadStrings = [
    'We are cooking up new features! 🍳',
    'We are seasoning our app with excellence! 🌶️',
    'We are stirring in fresh enhancements! 🥄',
    'We are rolling out the dough of innovation! 🍕',
    'We are whisking up some app magic! ✨',
    'We are baking fresh updates just for you! 🥖',
    'We are adding some extra cheese to our experience! 🧀',
    'We are mixing a fresh batch of capabilities! 🍲',
    'We are serving up a hot plate of newness! 🍽️',
    'App-etizers are on the way! 🥗',
    'We are tossing up some fresh new features! 🥗',
    'We are blending the best for our users! 🍹',
    'Order up! Fresh new features are coming your way! 📣',
    'We are sizzling up some app enhancements! 🔥',
    'We are whipping up a delightful experience! 🍦',
    'We are refreshing the digital menu! 🍹',
    'We are setting the table with new tools! 🍽️',
    'We are pouring a fresh cup of innovation! ☕',
    'We are dishing out the latest and greatest! 🍲',
  ];

  downloadCopy = this.downloadStrings[Math.floor(Math.random() * this.downloadStrings.length)];

  ngAfterViewInit() {
    setTimeout(async () => {
      await SplashScreen.hide({ fadeOutDuration: 300 });
      await this.startDownloadProcess();
    }, 1000);
  }

  async startDownloadProcess() {
    if (environment.debug) {
      await firstValueFrom(timer(2000));
      return this.finishDownload();
    }

    if (Capacitor.getPlatform() !== 'web') {
      try {
        // Always notify app ready for the current bundle first thing
        console.log('Calling notifyAppReady unconditionally at startup');
        await CapacitorUpdater.notifyAppReady();

        // Try to check for updates
        try {
          const shouldUpdate = await this.checkVersion();
          if (shouldUpdate) {
            this.currentState = DownloadState.FOUND;
            try {
              const newestVersion = await CapacitorUpdater.getLatest();
              // Only proceed if we actually got update info
              if (newestVersion && newestVersion.url) {
                this.currentState = DownloadState.DOWNLOADING;
                const version: BundleInfo = await CapacitorUpdater.download({
                  url: newestVersion.url,
                  version: newestVersion.version,
                });
                if (version !== null) {
                  try {
                    await SplashScreen.show({ autoHide: false });
                    await CapacitorUpdater.set(version);
                  } catch (error: any) {
                    console.error('Error setting version:', error);
                    await this.finishDownload();
                    return;
                  }
                } else {
                  await this.finishDownload();
                  return;
                }
              } else {
                console.log('No updates available or update info incomplete, continuing with current bundle');
                await this.finishDownload();
                return;
              }
            } catch (e) {
              console.error('Error during update process:', e);
              await this.finishDownload();
              return;
            }
          } else {
            console.log('No updates needed, continuing with current bundle');
            await this.finishDownload();
            return;
          }
        } catch (updateError) {
          // Handle update check errors gracefully
          console.log('Update check failed, continuing with current bundle:', updateError);
          await this.finishDownload();
          return;
        }
      } catch (e) {
        console.error('Critical error in startDownloadProcess:', e);
        // Make sure we call notifyAppReady even on errors
        try {
          await CapacitorUpdater.notifyAppReady();
        } catch (err) {
          console.error('Error in error-recovery notifyAppReady:', err);
        }
        await this.finishDownload();
        return;
      }
    } else {
      await this.finishDownload();
      return;
    }
  }

  async checkVersion() {
    try {
      const currentVersion = await CapacitorUpdater.current();
      let newestVersion;

      try {
        newestVersion = await CapacitorUpdater.getLatest();
      } catch (e) {
        console.log('Error getting latest version:', e);

        // Check specifically for channel_not_found error
        if (e.message === 'channel_not_found' || e.errorMessage === 'channel_not_found') {
          console.log('No update channel found - this is expected behavior in some environments');
          return false; // Don't try to update if no channel exists
        }

        // For any other error, log it and don't try to update
        return false;
      }

      // Only return true if we have valid update info
      if (newestVersion && newestVersion.version && newestVersion.url && currentVersion.bundle.version !== newestVersion.version) {
        return true;
      }

      return false;
    } catch (e) {
      console.error('Critical error checking version:', e);
      return false;
    }
  }

  async finishDownload() {
    try {
      // First notify that the app is ready - make sure this is called first
      console.log('Calling notifyAppReady in finishDownload');
      await CapacitorUpdater.notifyAppReady();
      console.log('Successfully notified app ready in finishDownload');

      if (Capacitor.getPlatform() !== 'web') {
        try {
          // Try to get stored credentials
          const username = await SecureStoragePlugin.get({ key: 'auth.username' });
          const password = await SecureStoragePlugin.get({ key: 'auth.password' });

          if (username && password) {
            try {
              // Wait for login to complete before dismissing the modal
              await this.store.dispatch(new SignIn(username.value, password.value)).toPromise();
              console.log('Auto-login successful');
            } catch (loginError) {
              // Handle verification error differently than other errors
              if (loginError && loginError.name === 'Waiting for Email Verification') {
                console.log('User needs to verify email, initializing as guest without restarting');
                // Don't clear tokens if it's just a verification issue
                await this.store.dispatch([new InitializeUser(), new SetAdditionalUserFields()]).toPromise();
              } else {
                // For other errors, initialize as guest
                console.error('Auto-login failed:', loginError);
                await this.store.dispatch([new InitializeUser(), new SetAdditionalUserFields()]).toPromise();
              }
            }
          } else {
            // Initialize as guest if no stored credentials
            await this.store.dispatch([new InitializeUser(), new SetAdditionalUserFields()]).toPromise();
          }
        } catch (e) {
          console.error('Error during auth initialization:', e);
          try {
            // Fallback initialization without aggressive token clearing
            await this.store.dispatch([new InitializeUser(), new SetAdditionalUserFields()]).toPromise();
          } catch (fallbackError) {
            console.error('Error during fallback initialization:', fallbackError);
            // Only clear tokens if absolutely necessary
            if (fallbackError && fallbackError.status >= 400) {
              await this.tokenManager.clearProviderTokens().toPromise();
            }
            await this.store.dispatch([new InitializeUser(), new SetAdditionalUserFields()]).toPromise();
          }
        }

        // Add a small delay to ensure UI updates complete
        await firstValueFrom(timer(500));
      }

      // Only now dismiss the modal and proceed
      await this.modalCtrl.dismiss();
      this.openOnboarding();
    } catch (error) {
      console.error('Critical error in finishDownload:', error);

      // Fatal error recovery - try to dismiss modal anyway so app is usable
      try {
        await this.modalCtrl.dismiss();
      } catch (e) {
        console.error('Could not dismiss modal during error recovery:', e);
      }
    }
  }

  openOnboarding() {
    this.onboardingSlides$
      .pipe(
        filter(onboardingSlides => !!onboardingSlides),
        take(1)
      )
      .subscribe(async onboardingSlides => {
        const data = await Storage.get({ key: 'seenOnboarding' });
        if (!JSON.parse(data.value)) {
          if (Capacitor.getPlatform() !== 'web' && onboardingSlides.length) {
            const onboarding = await this.modalCtrl.create({
              component: OnboardingSlidesComponent,
              keyboardClose: false,
              animated: true,
              mode: 'ios',
              backdropDismiss: false,
            });
            await onboarding.present();
          }
        }
      });
  }
}
